export type AhaBridgePlatform = 'ios' | 'android' | 'web';

export type AhaBridgeCapabilities = {
  runtime: number;
  auth: number;
  loading: number;
  file: number;
};

export type AhaBridgeInfo = {
  platform: 'ios' | 'android';
  appId: string;
  appVersion: string;
  buildNumber: number;
  ahaBridgeVersion: number;
  capabilities: AhaBridgeCapabilities;
};

export type AhaBridgeSaveResult = {
  ok?: boolean;
  uri?: string;
  fileName?: string;
  canceled?: boolean;
};

export type AhaBridgeDownloadProgress = {
  requestId?: string;
  progress?: number;
  loadedBytes?: number;
  totalBytes?: number;
  stage?: 'started' | 'downloading' | 'saving';
};

export type AhaBridgeListenerHandle = {
  remove: () => Promise<void> | void;
};

export type AhaBridgeFileOptions = {
  dataUrl: string;
  fileName: string;
  mimeType: string;
  mediaKind?: 'image' | 'video' | 'file';
  saveToPhotos?: boolean;
};

export type AhaBridgeSaveUrlOptions = {
  requestId?: string;
  url: string;
  fileName: string;
  mimeType?: string;
  mediaKind?: 'image' | 'video' | 'file';
  saveToPhotos?: boolean;
};

export type AhaBridgeAuthCapabilities = {
  ok?: boolean;
  systemBrowser?: boolean;
  customTabs?: boolean;
  warmupUnsupported?: boolean;
};

export type AhaBridge = {
  version: number;
  platform: AhaBridgePlatform;
  capabilities: AhaBridgeCapabilities;
  isNative: boolean;
  supports: (
    module: keyof AhaBridgeCapabilities,
    minVersion?: number
  ) => boolean;
  runtime: {
    getInfo: () => Promise<AhaBridgeInfo | null>;
    openAbout: () => Promise<boolean>;
  };
  auth: {
    openExternalBrowser: (options: { url: string }) => Promise<boolean>;
    warmup: (options?: { url?: string }) => Promise<boolean>;
    getCapabilities: () => Promise<AhaBridgeAuthCapabilities>;
  };
  loading: {
    start: (options?: {
      title?: string;
      maxVisibleMs?: number;
    }) => Promise<boolean>;
    end: () => Promise<boolean>;
  };
  file: {
    saveFile: (
      options: AhaBridgeFileOptions
    ) => Promise<AhaBridgeSaveResult | null>;
    saveImage: (options: {
      dataUrl: string;
      fileName: string;
    }) => Promise<AhaBridgeSaveResult | null>;
    saveUrl: (
      options: AhaBridgeSaveUrlOptions
    ) => Promise<AhaBridgeSaveResult | null>;
    onDownloadProgress: (
      listener: (progress: AhaBridgeDownloadProgress) => void
    ) => Promise<AhaBridgeListenerHandle | null>;
  };
};

type NativeAhaBridgePlugin = {
  getInfo?: (options?: Record<string, never>) => Promise<AhaBridgeInfo>;
  openAbout?: (options?: Record<string, never>) => Promise<{ ok?: boolean }>;
  openExternalBrowser?: (options: { url: string }) => Promise<{ ok?: boolean }>;
  warmupExternalBrowser?: (options?: {
    url?: string;
  }) => Promise<{ ok?: boolean }>;
  getExternalBrowserCapabilities?: (
    options?: Record<string, never>
  ) => Promise<AhaBridgeAuthCapabilities>;
  startLoading?: (options?: {
    title?: string;
    maxVisibleMs?: number;
  }) => Promise<{ ok?: boolean }>;
  endLoading?: (options?: Record<string, never>) => Promise<{ ok?: boolean }>;
  saveFile?: (options: AhaBridgeFileOptions) => Promise<AhaBridgeSaveResult>;
  saveImage?: (options: {
    dataUrl: string;
    fileName: string;
  }) => Promise<AhaBridgeSaveResult>;
  saveUrl?: (options: AhaBridgeSaveUrlOptions) => Promise<AhaBridgeSaveResult>;
  addListener?: (
    eventName: 'downloadProgress',
    listener: (progress: AhaBridgeDownloadProgress) => void
  ) => Promise<AhaBridgeListenerHandle> | AhaBridgeListenerHandle;
};

type CapacitorGlobal = {
  getPlatform?: () => string;
  isNativePlatform?: () => boolean;
  Plugins?: {
    AhaBridge?: NativeAhaBridgePlugin;
  };
};

declare global {
  interface Window {
    ahaBridge: AhaBridge;
    Capacitor?: CapacitorGlobal;
  }
}

const AHA_BRIDGE_VERSION = 1;
const AHA_BRIDGE_CAPABILITIES: AhaBridgeCapabilities = {
  runtime: 1,
  auth: 1,
  loading: 1,
  file: 1
};

const getCapacitor = () =>
  typeof window === 'undefined' ? undefined : window.Capacitor;

const getNativePlugin = () => getCapacitor()?.Plugins?.AhaBridge || null;

export const hasNativeAhaBridge = () => !!getNativePlugin();

const isNativeCapacitor = () => {
  const capacitor = getCapacitor();
  if (!capacitor) return false;
  if (typeof capacitor.isNativePlatform === 'function') {
    return !!capacitor.isNativePlatform();
  }
  return !!capacitor.Plugins?.AhaBridge;
};

const getPlatform = (): AhaBridgePlatform => {
  const platform = getCapacitor()?.getPlatform?.();
  if (platform === 'ios' || platform === 'android') return platform;
  return 'web';
};

const requireNativePlugin = () => {
  const plugin = getNativePlugin();
  if (!plugin && isNativeCapacitor()) {
    throw new Error('AhaBridge native plugin is unavailable.');
  }
  return plugin;
};

export const NATIVE_BRIDGE_UPGRADE_PATH = '/app/upgrade-required';

export const shouldForceNativeBridgeUpgrade = () => {
  const capacitor = getCapacitor();
  if (!capacitor) return false;
  const isNative =
    typeof capacitor.isNativePlatform === 'function'
      ? !!capacitor.isNativePlatform()
      : !!capacitor.Plugins;
  return isNative && !hasNativeAhaBridge();
};

const normalizeBridgeInfo = (raw: AhaBridgeInfo): AhaBridgeInfo => ({
  platform: raw.platform,
  appId: String(raw.appId || '').trim(),
  appVersion: String(raw.appVersion || '').trim(),
  buildNumber: Number(raw.buildNumber) || 0,
  ahaBridgeVersion: Number(raw.ahaBridgeVersion) || 0,
  capabilities: {
    ...AHA_BRIDGE_CAPABILITIES,
    ...(raw.capabilities || {})
  }
});

const createAhaBridge = (): AhaBridge => {
  const bridge: AhaBridge = {
    version: AHA_BRIDGE_VERSION,
    platform: getPlatform(),
    capabilities: AHA_BRIDGE_CAPABILITIES,
    get isNative() {
      return isNativeCapacitor();
    },
    supports(module, minVersion = 1) {
      return (bridge.capabilities[module] || 0) >= minVersion;
    },
    runtime: {
      async getInfo() {
        const plugin = requireNativePlugin();
        if (!plugin) return null;
        if (typeof plugin.getInfo !== 'function') {
          throw new Error('AhaBridge.runtime.getInfo is unavailable.');
        }
        return normalizeBridgeInfo(await plugin.getInfo({}));
      },
      async openAbout() {
        const plugin = requireNativePlugin();
        if (!plugin) return false;
        if (typeof plugin.openAbout !== 'function') {
          throw new Error('AhaBridge.runtime.openAbout is unavailable.');
        }
        await plugin.openAbout({});
        return true;
      }
    },
    auth: {
      async openExternalBrowser(options) {
        const plugin = requireNativePlugin();
        if (!plugin) return false;
        if (typeof plugin.openExternalBrowser !== 'function') {
          throw new Error('AhaBridge.auth.openExternalBrowser is unavailable.');
        }
        await plugin.openExternalBrowser(options);
        return true;
      },
      async warmup(options = {}) {
        const plugin = requireNativePlugin();
        if (!plugin) return false;
        if (typeof plugin.warmupExternalBrowser !== 'function') return false;
        const result = await plugin.warmupExternalBrowser(options);
        return result?.ok !== false;
      },
      async getCapabilities() {
        const plugin = requireNativePlugin();
        if (!plugin) return {};
        if (typeof plugin.getExternalBrowserCapabilities !== 'function') {
          throw new Error('AhaBridge.auth.getCapabilities is unavailable.');
        }
        return plugin.getExternalBrowserCapabilities({});
      }
    },
    loading: {
      async start(options = {}) {
        const plugin = requireNativePlugin();
        if (!plugin) return false;
        if (typeof plugin.startLoading !== 'function') {
          throw new Error('AhaBridge.loading.start is unavailable.');
        }
        await plugin.startLoading(options);
        return true;
      },
      async end() {
        const plugin = requireNativePlugin();
        if (!plugin) return false;
        if (typeof plugin.endLoading !== 'function') {
          throw new Error('AhaBridge.loading.end is unavailable.');
        }
        await plugin.endLoading({});
        return true;
      }
    },
    file: {
      async saveFile(options) {
        const plugin = requireNativePlugin();
        if (!plugin) return null;
        if (typeof plugin.saveFile !== 'function') {
          throw new Error('AhaBridge.file.saveFile is unavailable.');
        }
        return plugin.saveFile(options);
      },
      async saveImage(options) {
        const plugin = requireNativePlugin();
        if (!plugin) return null;
        if (typeof plugin.saveImage !== 'function') {
          throw new Error('AhaBridge.file.saveImage is unavailable.');
        }
        return plugin.saveImage(options);
      },
      async saveUrl(options) {
        const plugin = requireNativePlugin();
        if (!plugin) return null;
        if (typeof plugin.saveUrl !== 'function') {
          throw new Error('AhaBridge.file.saveUrl is unavailable.');
        }
        return plugin.saveUrl(options);
      },
      async onDownloadProgress(listener) {
        const plugin = requireNativePlugin();
        if (!plugin || typeof plugin.addListener !== 'function') return null;
        return plugin.addListener('downloadProgress', listener);
      }
    }
  };

  return bridge;
};

export const installAhaBridge = () => {
  if (typeof window === 'undefined') return null;
  if (!window.ahaBridge) {
    window.ahaBridge = createAhaBridge();
  }
  return window.ahaBridge;
};

export const getAhaBridge = () => {
  const bridge = installAhaBridge();
  if (!bridge) {
    throw new Error('window.ahaBridge is unavailable outside the browser.');
  }
  return bridge;
};

installAhaBridge();
